import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Popover,
  Grid,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { LocalizationProvider, CalendarPicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  format,
  addMonths,
  startOfMonth,
  isSameDay,
  isAfter,
  isBefore,
  addDays,
  isSameMonth,
  startOfDay,
} from 'date-fns';
import { dateUtils } from '../../../utils/DateUtils';
import {
  DATE_UNAVAILABILITY_REASON,
  dateAvailabilityStatusPropType,
  dateToIsoKey,
} from '../../../utils/availabilityStatus';
import CalendarDayCell from './calendar/CalendarDayCell';
import { getCheckinDayMeta, isNoInventoryStatus } from './calendar/calendarDayMeta';
import { calendarPickerChromeSx } from './calendar/calendarDayStyles';
import {
  DAY_NAV_KEYS,
  focusVisibleCalendarDayButton,
  getDayNavigationTarget,
} from './calendar/calendarKeyboardNavigation';
import LoadingScreen from '../../../common/LoadingScreen';
import {
  MAX_AVAILABILITY_MONTHS_AHEAD,
  MAX_RESERVATION_NIGHTS,
} from '../../../common/bookingConstants';
import PropTypes from 'prop-types';

const formatDay = (date) => (date ? format(date, 'EEE d MMM') : '');

const MONTHS_TO_SHOW_MOBILE = MAX_AVAILABILITY_MONTHS_AHEAD;

/**
 * Dual-field check-in / check-out date selector with a popover calendar.
 *
 * Behaviour overview:
 * - First click sets check-in; second click (on/after check-in) sets check-out and closes.
 * - While check-in is set and check-out is not, clicking a day **before** check-in starts a new check-in.
 * - Optional availability maps drive disabled days, visual tiers, and restriction tooltips.
 * - Desktop shows two months side by side; mobile shows a vertical stack of months.
 *
 * Extra props are forwarded to both underlying `TextField`s.
 *
 * @param {object} props
 * @param {Date|null} [props.checkin] - selected arrival date
 * @param {Date|null} [props.checkout] - selected departure date
 * @param {(value: { checkin: Date|null, checkout: Date|null }) => void} props.onChange - called when check-in and/or check-out change
 * @param {Date} [props.minDate] - earliest selectable calendar day (also anchors the max-ahead horizon)
 * @param {boolean} [props.disabled] - disables both date fields and prevents opening the calendar
 * @param {(payload: { open: boolean, months: Date[] }) => void} [props.onVisibleMonthsChange] - fired when the popover opens/closes or the visible month window changes (`months`: month starts currently shown - desktop: 2; mobile: 12)
 * @param {import('../../../utils/availabilityStatus').AvailabilityByDateMap} [props.checkinAvailabilityByDate] - from checkinAvailability; used while selecting arrival (disable rules, gray tiers, tooltips)
 * @param {import('../../../utils/availabilityStatus').AvailabilityByDateMap} [props.checkoutAvailabilityByDate] - from checkoutAvailability; used while selecting departure after check-in is set
 * @param {boolean} [props.isCheckinAvailabilityLoading=false] - shows a loading overlay on the calendar
 * @param {boolean} [props.isCheckoutAvailabilityLoading=false] - shows a loading overlay on the calendar
 * @returns {JSX.Element}
 */
const MultiDateSelector = ({
  checkin,
  checkout,
  onChange,
  minDate,
  disabled,
  onVisibleMonthsChange,
  checkinAvailabilityByDate,
  checkoutAvailabilityByDate,
  isCheckinAvailabilityLoading = false,
  isCheckoutAvailabilityLoading = false,
  ...props
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCalendarAvailabilityLoading =
    isCheckinAvailabilityLoading || isCheckoutAvailabilityLoading;
  const maxBookableDate = useMemo(() => {
    const base = minDate ?? new Date();
    return startOfDay(addMonths(startOfDay(base), MAX_AVAILABILITY_MONTHS_AHEAD));
  }, [minDate]);
  const [anchorEl, setAnchorEl] = useState(null);
  // Single source of truth: left calendar month. Right calendar always shows currentMonth + 1.
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  /** Day under the mouse — drives checkout-candidate preview and range highlight while picking checkout. */
  const [hoveredDate, setHoveredDate] = useState(null);

  const open = Boolean(anchorEl);

  const getVisibleCalendarMonths = useCallback(() => {
    if (isSmallScreen) {
      return Array.from({ length: MONTHS_TO_SHOW_MOBILE }, (_, i) => addMonths(currentMonth, i));
    }
    return [currentMonth, addMonths(currentMonth, 1)];
  }, [isSmallScreen, currentMonth]);

  useEffect(() => {
    if (!onVisibleMonthsChange) return;
    if (open) {
      onVisibleMonthsChange({ open: true, months: getVisibleCalendarMonths() });
    } else {
      onVisibleMonthsChange({ open: false, months: [] });
    }
  }, [open, currentMonth, isSmallScreen, onVisibleMonthsChange, getVisibleCalendarMonths]);

  const mobileMonthDates = useMemo(() => {
    return Array.from({ length: MONTHS_TO_SHOW_MOBILE }, (_, i) => addMonths(currentMonth, i));
  }, [currentMonth]);

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      const initial = checkin || checkout || minDate || new Date();
      setCurrentMonth(startOfMonth(initial));
      setHoveredDate(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setHoveredDate(null);
  };

  const handleDateSelect = useCallback((date) => {
    // If checkin is selected but checkout not, next selection defines checkout
    // (or a new checkin when the clicked day is before the current checkin).
    if (checkin && !checkout) {
      if (isBefore(date, checkin)) {
        onChange({ checkin: date, checkout: null });
        setHoveredDate(null);
        return;
      }
      if (checkoutAvailabilityByDate) {
        const status = checkoutAvailabilityByDate[dateToIsoKey(date)];
        if (!status || !status.available) {
          return;
        }
        onChange({ checkin, checkout: date });
        handleClose();
        return;
      }
      const minCheckout = addDays(checkin, 2);
      const maxCheckout = addDays(checkin, MAX_RESERVATION_NIGHTS);
      if (isBefore(date, minCheckout)) {
        return;
      }
      if (isAfter(startOfDay(date), startOfDay(maxCheckout))) {
        return;
      }
      onChange({ checkin, checkout: date });
      handleClose();
    } else {
      onChange({ checkin: date, checkout: null });
      setHoveredDate(null);
    }
  }, [checkin, checkout, checkoutAvailabilityByDate, onChange]);

  const handleDateHover = useCallback((date) => {
    setHoveredDate(date);
  }, []);

  const handleCalendarMouseLeave = useCallback(() => {
    setHoveredDate(null);
  }, []);

  const handleClear = () => {
    setHoveredDate(null);
    onChange({ checkin: null, checkout: null });
  };

  const isPickingCheckin = useMemo(
    () => (!checkin && !checkout) || (Boolean(checkin) && Boolean(checkout)),
    [checkin, checkout]
  );

  const isPickingCheckout = useMemo(
    () => Boolean(checkin) && !checkout,
    [checkin, checkout]
  );

  const isAvailableCheckoutDay = useCallback((date) => {
    if (!checkin || isBefore(date, checkin) || isSameDay(date, checkin)) {
      return false;
    }
    if (checkoutAvailabilityByDate) {
      const status = checkoutAvailabilityByDate[dateToIsoKey(date)];
      return Boolean(status?.available);
    }
    const minCheckout = addDays(checkin, 2);
    const maxCheckout = addDays(checkin, MAX_RESERVATION_NIGHTS);
    return !isBefore(date, minCheckout)
      && !isAfter(startOfDay(date), startOfDay(maxCheckout));
  }, [checkin, checkoutAvailabilityByDate]);

  /** End of the visible stay range: confirmed checkout, or hovered available checkout while picking. */
  const rangeEndDate = useMemo(() => {
    if (checkout) return checkout;
    if (isPickingCheckout && hoveredDate && isAvailableCheckoutDay(hoveredDate)) {
      return hoveredDate;
    }
    return null;
  }, [checkout, isPickingCheckout, hoveredDate, isAvailableCheckoutDay]);

  const isCheckoutCandidateDay = useCallback((date) => (
    Boolean(
      isPickingCheckout
      && hoveredDate
      && isSameDay(date, hoveredDate)
      && isAvailableCheckoutDay(hoveredDate)
    )
  ), [isPickingCheckout, hoveredDate, isAvailableCheckoutDay]);

  /**
   * Availability unavailability reason for styling/tooltip, or null when none applies.
   * Check-in restrictions are normalized to CHECKIN_DAY_NOT_ALLOWED (generic tooltip).
   * Checkout mode: before check-in uses check-in map; NO_INVENTORY from check-in wins;
   * missing checkout-map entries → MAX_LOS_EXCEEDED.
   * @returns {{ reason: string|null, availabilityStatus: object|null }}
   */
  const getDisabledDayMeta = useCallback((date) => {
    const key = dateToIsoKey(date);

    if (isPickingCheckin) {
      if (!checkinAvailabilityByDate) {
        return { reason: null, availabilityStatus: null };
      }
      return getCheckinDayMeta(checkinAvailabilityByDate[key]);
    }

    if (!isPickingCheckout) {
      return { reason: null, availabilityStatus: null };
    }

    // Dates before check-in keep check-in visual status (no tooltip).
    if (isBefore(startOfDay(date), startOfDay(checkin))) {
      if (!checkinAvailabilityByDate) {
        return { reason: null, availabilityStatus: null };
      }
      const meta = getCheckinDayMeta(checkinAvailabilityByDate[key]);
      return { reason: meta.reason, availabilityStatus: { suppressTooltip: true } };
    }

    // Selected check-in day: no availability overlay (highlighted via selected styling).
    if (isSameDay(date, checkin)) {
      return { reason: null, availabilityStatus: null };
    }

    const checkinStatus = checkinAvailabilityByDate?.[key];
    if (isNoInventoryStatus(checkinStatus)) {
      return {
        reason: DATE_UNAVAILABILITY_REASON.NO_INVENTORY,
        availabilityStatus: null,
      };
    }

    if (!checkoutAvailabilityByDate) {
      return { reason: null, availabilityStatus: null };
    }

    const checkoutStatus = checkoutAvailabilityByDate[key];
    if (!checkoutStatus) {
      return {
        reason: DATE_UNAVAILABILITY_REASON.MAX_LOS_EXCEEDED,
        availabilityStatus: null,
      };
    }
    if (checkoutStatus.available) {
      return { reason: null, availabilityStatus: null };
    }
    if (checkoutStatus.reason === DATE_UNAVAILABILITY_REASON.NO_INVENTORY) {
      return {
        reason: DATE_UNAVAILABILITY_REASON.NO_INVENTORY,
        availabilityStatus: null,
      };
    }
    return {
      reason: checkoutStatus.reason || DATE_UNAVAILABILITY_REASON.NO_VALID_STAY,
      availabilityStatus: checkoutStatus,
    };
  }, [
    isPickingCheckin,
    isPickingCheckout,
    checkin,
    checkinAvailabilityByDate,
    checkoutAvailabilityByDate,
  ]);

  const shouldDisableDateFn = useCallback((date) => {
    const dayStart = startOfDay(date);
    if (isAfter(dayStart, maxBookableDate)) {
      return true;
    }
    if (minDate && isBefore(dayStart, startOfDay(minDate))) {
      return true;
    }

    if (isPickingCheckout) {
      // Before check-in: selectable as a new check-in when check-in-available.
      if (isBefore(dayStart, startOfDay(checkin))) {
        if (checkinAvailabilityByDate) {
          const status = checkinAvailabilityByDate[dateToIsoKey(date)];
          if (!status || !status.available) {
            return true;
          }
        }
        return false;
      }

      // Keep the selected check-in focusable (disabling it steals focus to the previous day).
      // Selecting it as checkout is blocked in handleDateSelect.
      if (isSameDay(date, checkin)) {
        return false;
      }

      const key = dateToIsoKey(date);
      if (isNoInventoryStatus(checkinAvailabilityByDate?.[key])) {
        return true;
      }

      if (checkoutAvailabilityByDate) {
        const status = checkoutAvailabilityByDate[key];
        if (!status || !status.available) {
          return true;
        }
        return false;
      }

      const minCheckout = addDays(checkin, 2);
      const maxCheckout = addDays(checkin, MAX_RESERVATION_NIGHTS);
      return isBefore(dayStart, startOfDay(minCheckout))
        || isAfter(dayStart, startOfDay(maxCheckout));
    }

    if (checkinAvailabilityByDate && isPickingCheckin) {
      const status = checkinAvailabilityByDate[dateToIsoKey(date)];
      if (!status || !status.available) {
        return true;
      }
    }
    return false;
  }, [
    minDate,
    maxBookableDate,
    checkin,
    isPickingCheckin,
    isPickingCheckout,
    checkinAvailabilityByDate,
    checkoutAvailabilityByDate,
  ]);

  const canGoBack = useCallback(() => {
    if (!minDate) return true;
    const minMonth = startOfMonth(minDate);
    return isAfter(currentMonth, minMonth);
  }, [minDate, currentMonth]);

  const canGoForward = useCallback(() => {
    if (isSmallScreen) return true;
    const nextLeftMonth = addMonths(currentMonth, 1);
    return !isAfter(startOfMonth(nextLeftMonth), startOfMonth(maxBookableDate));
  }, [isSmallScreen, currentMonth, maxBookableDate]);

  const handlePrevMonth = () => {
    if (isCalendarAvailabilityLoading) return;
    const prevMonth = addMonths(currentMonth, -1);
    const minMonth = minDate ? startOfMonth(minDate) : null;
    if (!minMonth || isAfter(prevMonth, minMonth) || isSameMonth(prevMonth, minMonth)) {
      setCurrentMonth(prevMonth);
    }
  };

  const handleNextMonth = () => {
    if (isCalendarAvailabilityLoading || !canGoForward()) return;
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  // When right calendar's month changes, sync: left = newMonth-1, right = newMonth
  const handleRightMonthChange = useCallback((newMonth) => {
    if (isCalendarAvailabilityLoading) return;
    setCurrentMonth(startOfMonth(addMonths(newMonth, -1)));
  }, [isCalendarAvailabilityLoading]);

  const hasRange = Boolean(checkin && rangeEndDate && isAfter(rangeEndDate, checkin));

  /** Day to focus after a dual-calendar window swipe (set before `setCurrentMonth`). */
  const pendingFocusDayRef = useRef(null);

  useEffect(() => {
    const day = pendingFocusDayRef.current;
    if (!day) return;
    pendingFocusDayRef.current = null;
    requestAnimationFrame(() => {
      focusVisibleCalendarDayButton(day);
    });
  }, [currentMonth]);

  /**
   * Intercept arrow keys that would land on an outsideCurrentMonth day.
   * - Left pane → focus the day in the right pane (no window swipe)
   * - Right pane → swipe the dual-month window, then focus the day
   * - Mobile (no position) → focus the in-month cell in the month stack
   * @returns {boolean} true if the event was handled
   */
  const handleDayKeyDown = useCallback((event, day, calendarPosition, calendarMonth) => {
    if (!DAY_NAV_KEYS.has(event.key) || !calendarMonth) {
      return false;
    }

    const targetDay = getDayNavigationTarget(event.key, day);
    if (!targetDay || isSameMonth(targetDay, calendarMonth)) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isCalendarAvailabilityLoading) {
      return true;
    }

    const goingForward = isAfter(startOfMonth(targetDay), startOfMonth(calendarMonth));
    const step = goingForward ? 1 : -1;

    let focusDay = targetDay;
    if (shouldDisableDateFn(focusDay)) {
      focusDay = null;
      let cursor = targetDay;
      for (let i = 0; i < 62; i += 1) {
        cursor = addDays(cursor, step);
        if (!shouldDisableDateFn(cursor)) {
          focusDay = cursor;
          break;
        }
      }
    }
    if (!focusDay) {
      return true;
    }

    // Mobile stacked months: jump focus to the real in-month button.
    if (calendarPosition !== 'left' && calendarPosition !== 'right') {
      requestAnimationFrame(() => focusVisibleCalendarDayButton(focusDay));
      return true;
    }

    if (goingForward) {
      if (calendarPosition === 'left') {
        requestAnimationFrame(() => focusVisibleCalendarDayButton(focusDay));
        return true;
      }
      if (!canGoForward()) {
        return true;
      }
      pendingFocusDayRef.current = focusDay;
      setCurrentMonth(addMonths(currentMonth, 1));
      return true;
    }

    if (calendarPosition === 'right') {
      requestAnimationFrame(() => focusVisibleCalendarDayButton(focusDay));
      return true;
    }
    if (!canGoBack()) {
      return true;
    }
    pendingFocusDayRef.current = focusDay;
    setCurrentMonth(addMonths(currentMonth, -1));
    return true;
  }, [
    isCalendarAvailabilityLoading,
    shouldDisableDateFn,
    currentMonth,
    canGoBack,
    canGoForward,
  ]);

  const renderCalendarDay = useCallback((day, pickersDayProps, calendarPosition, calendarMonth) => {
    // Hidden trailing/leading cells for adjacent months — must not get range/selection chrome.
    const outsideCurrentMonth = Boolean(pickersDayProps.outsideCurrentMonth);
    const dayStart = startOfDay(day);
    const isCheckinDay = Boolean(!outsideCurrentMonth && checkin && isSameDay(day, checkin));
    const isCheckoutDay = Boolean(!outsideCurrentMonth && checkout && isSameDay(day, checkout));
    const checkoutCandidate = !outsideCurrentMonth && isCheckoutCandidateDay(day);
    const selected = isCheckinDay || isCheckoutDay || checkoutCandidate;
    const inRange = Boolean(
      !outsideCurrentMonth
      && hasRange
      && !isBefore(dayStart, startOfDay(checkin))
      && !isAfter(dayStart, startOfDay(rangeEndDate))
    );
    const isRangeStart = hasRange && isCheckinDay;
    const isRangeEnd = hasRange && (isCheckoutDay || checkoutCandidate);
    const disabled = outsideCurrentMonth || shouldDisableDateFn(day);
    const { reason: disabledReason, availabilityStatus } = outsideCurrentMonth
      ? { reason: null, availabilityStatus: null }
      : getDisabledDayMeta(day);

    // Static styles cover everything but the rounded outer corners of the range (at most 2 cells).
    let rootBorderRadius;
    if (isRangeStart && isRangeEnd) rootBorderRadius = '50%';
    else if (isRangeStart) rootBorderRadius = '50% 0 0 50%';
    else if (isRangeEnd) rootBorderRadius = '0 50% 50% 0';
    const sx = rootBorderRadius ? { root: { borderRadius: rootBorderRadius } } : undefined;

    return (
      <CalendarDayCell
        day={day}
        pickersDayProps={pickersDayProps}
        selected={selected}
        inRange={inRange}
        disabled={disabled}
        disabledReason={disabledReason}
        sx={sx}
        onSelect={handleDateSelect}
        onMouseOver={handleDateHover}
        onKeyDown={(event, focusedDay) => handleDayKeyDown(
          event,
          focusedDay,
          calendarPosition,
          calendarMonth
        )}
        availabilityStatus={availabilityStatus}
      />
    );
  }, [
    checkin,
    checkout,
    hasRange,
    rangeEndDate,
    isCheckoutCandidateDay,
    shouldDisableDateFn,
    getDisabledDayMeta,
    handleDateSelect,
    handleDateHover,
    handleDayKeyDown,
  ]);

  const renderDayLeft = useCallback(
    (day, _selectedDate, pickersDayProps) => (
      renderCalendarDay(day, pickersDayProps, 'left', currentMonth)
    ),
    [renderCalendarDay, currentMonth]
  );
  const renderDayRight = useCallback(
    (day, _selectedDate, pickersDayProps) => (
      renderCalendarDay(day, pickersDayProps, 'right', addMonths(currentMonth, 1))
    ),
    [renderCalendarDay, currentMonth]
  );

  return (
    <>
      {/* Two separate input fields – label uses background to create notch so border stays tight */}
      <Box sx={{
        display: 'flex',
      }}>
        <TextField
          {...props}
          variant="outlined"
          label="Przyjazd"
          value={formatDay(checkin)}
          InputProps={{
            readOnly: true,
            sx: {
              cursor: disabled ? 'not-allowed' : 'pointer',
              '& fieldset': { 
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                borderRightWidth: '0.5px',
              },
            },
          }}
          disabled={disabled}
          sx={{
            flex: 1
          }}
          onClick={handleClick}
        />
        <TextField
          {...props}
          variant="outlined"
          label="Wyjazd"
          value={formatDay(checkout)}
          InputProps={{
            readOnly: true,
            sx: {
              cursor: disabled ? 'not-allowed' : 'pointer',
              '& fieldset': { 
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
                borderLeftWidth: '0.5px',
              },
            },
          }}
          disabled={disabled}
          sx={{
            flex: 1
          }}
          onClick={handleClick}
        />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: isSmallScreen ? 280 : 600,
            maxWidth: isSmallScreen ? 'calc(100vw - 32px)' : undefined,
            width: isSmallScreen ? 'auto' : undefined,
            maxHeight: isSmallScreen ? '70vh' : undefined,
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box {...(isCalendarAvailabilityLoading ? { sx: { position: 'relative' } } : {})}>
            {isSmallScreen ? (
              /* Small screen: vertical stack of 12 months, scrollable, no arrows */
              <Box
                sx={{
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pr: 0.5,
                }}
              >
                {mobileMonthDates.map((monthDate) => (
                  <Box
                    key={monthDate.toISOString()}
                    onMouseLeave={handleCalendarMouseLeave}
                    sx={calendarPickerChromeSx}
                  >
                    <CalendarPicker
                      date={null}
                      onChange={handleDateSelect}
                      defaultCalendarMonth={monthDate}
                      shouldDisableDate={shouldDisableDateFn}
                      renderDay={(day, _selectedDate, pickersDayProps) => (
                        renderCalendarDay(day, pickersDayProps, undefined, monthDate)
                      )}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              /* Desktop: two months side by side with arrows */
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                onMouseLeave={handleCalendarMouseLeave}
              >
                <IconButton
                  onClick={handlePrevMonth}
                  disabled={!canGoBack() || isCalendarAvailabilityLoading}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <ArrowBackIos fontSize="small" />
                </IconButton>

                <Grid container spacing={2} sx={{ flex: 1 }}>
                  <Grid item xs={6}>
                    <Box sx={calendarPickerChromeSx}>
                      <CalendarPicker
                        key={`left-${currentMonth.getTime()}`}
                        date={null}
                        onChange={handleDateSelect}
                        defaultCalendarMonth={currentMonth}
                        shouldDisableDate={shouldDisableDateFn}
                        renderDay={renderDayLeft}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={calendarPickerChromeSx}>
                      <CalendarPicker
                        key={`right-${currentMonth.getTime()}`}
                        date={null}
                        onChange={handleDateSelect}
                        defaultCalendarMonth={addMonths(currentMonth, 1)}
                        onMonthChange={handleRightMonthChange}
                        shouldDisableDate={shouldDisableDateFn}
                        renderDay={renderDayRight}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <IconButton
                  onClick={handleNextMonth}
                  disabled={!canGoForward() || isCalendarAvailabilityLoading}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Footer with actions */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mt: 2,
              pt: 2,
              pb: 2,
              borderTop: 1,
              borderColor: 'divider',
              minHeight: '80px',
              height: '80px',
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Zameldowanie: {formatDay(checkin) || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Wymeldowanie: {formatDay(checkout || rangeEndDate) || '—'}
                </Typography>
                {checkin && (checkout || rangeEndDate) && (
                  <Typography variant="body2" color="primary.main" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {(() => {
                      const n = dateUtils.getLengthOfStay(checkin, checkout || rangeEndDate);
                      return `${n} ${n === 1 ? 'noc' : n < 5 ? 'noce' : 'nocy'}`;
                    })()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                {(checkin || checkout) && (
                  <Button size="small" onClick={handleClear}>
                    Wyczyść
                  </Button>
                )}
              </Box>
            </Box>
            {isCalendarAvailabilityLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: (theme) => theme.zIndex.modal,
                }}
              >
                <LoadingScreen />
              </Box>
            )}
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

MultiDateSelector.propTypes = {
  checkin: PropTypes.instanceOf(Date),
  checkout: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  onVisibleMonthsChange: PropTypes.func,
  checkinAvailabilityByDate: PropTypes.objectOf(dateAvailabilityStatusPropType),
  checkoutAvailabilityByDate: PropTypes.objectOf(dateAvailabilityStatusPropType),
  isCheckinAvailabilityLoading: PropTypes.bool,
  isCheckoutAvailabilityLoading: PropTypes.bool,
};

export default MultiDateSelector;
